import { FC, KeyboardEvent, useEffect, useState, useMemo } from 'react'
import { toast } from 'react-toastify'
import en from 'src/constants/en'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { useGetKeywordsWithStatsQuery } from 'src/reactQuery/useGetKeywordsWithStatsQuery'
import { useGetCampaignsQuery } from 'src/reactQuery/useGetCampaignsQuery'
import {
  CampaignPayload,
  CampaignPayloadKeyword,
  CampaignType,
  KeywordFetchKeywordsWithStats,
} from 'src/services/__generated__/api'
import { PageBackLink, PageTitle } from 'src/components/PageBackLink'
import { AddNewCard } from 'src/components/AddNewCard'
import { Skeleton } from 'src/components/Skeleton'
import { BaseModal } from 'src/containers/BaseModal'
import { AnalyticsApi } from 'src/services/analyticsApi'
import { getDateRangeByQueryParams } from 'src/utils/getDateRangeByQueryParams'
import { useQueryParams } from 'src/hooks/useQueryParams'
import { GlobalQueryParams } from 'src/interfaces/globalQueryParams.interface'
import { TabsSection } from 'src/pages/AIListening/Main/TabsSection'
import { KeywordsFilterSection } from './KeywordsFilterSection'
import { sortFilteredDataByOrder } from './utils/sortFilteredDataByOrder'
import { FilteredDataItem } from './interfaces/filteredData.interface'
import { KeywordCardItem } from './KeywordCardItem'
import { createCards } from './utils/createCards'
import { ORDER_BY } from './constants/orderBy'
import styles from './keywords.module.scss'

// .keywordCardItem is 263px tall (keywordCardItem.module.scss) and .cardRow is
// a 2-column grid, so four skeletons fill two rows — enough to hold the region
// open without pretending to know how many keywords will arrive.
const KEYWORD_CARD_HEIGHT = 263
const KEYWORD_CARD_RADIUS = 16
const KEYWORD_SKELETON_COUNT = 4
import { ChooseCampaignModalContent } from './ChooseCampaignModalContent'
import { AddNewKeywordModalContent } from './AddNewKeywordModalContent'
import { KeywordsHeroSection } from './KeywordsHeroSection'
import { removeFromArray } from '../../utils/removeFromArray'

export const KeywordsPage: FC = () => {
  const sessionId = getSessionIdFromLocalStorage()

  const { queryParams } = useQueryParams<GlobalQueryParams>()

  const dates = getDateRangeByQueryParams(queryParams)

  const startDate = dates?.start
  const endDate = dates?.end

  const [filteredData, setFilteredData] = useState<FilteredDataItem[]>([])

  const [activeNum, setActiveNum] = useState<number>(0)

  const [completedNum, setCompletedNum] = useState<number>(0)

  const [allCardsNum, setAllCardsNum] = useState<number>(0)

  const [selectedCampaignsForNewKeyword, setSelectedCampaignForNewKeyword] =
    useState<string[]>([])

  const [showModal1, setShowModal1] = useState(false)

  const [showModal2, setShowModal2] = useState(false)

  const [selectedCampaigns, setSelectedCampaigns] = useState<
    CampaignType[] | null
  >(null)

  const [currentKeywordInput, setCurrentKeywordInput] = useState<string>('')

  const [addedKeywordsArr, setAddedKeywordsArr] = useState<string[]>([])

  const [registeredKeywordsArr, setRegisteredKeywordsArr] = useState<
    CampaignPayloadKeyword[]
  >([])

  const [filterOpen, setFilterOpen] = useState(false)

  const [orderFilter, setOrderFilter] = useState<string>(ORDER_BY[2])

  const [themesFilterArr, setThemesFilterArr] = useState<Array<any>>([])

  const [searchInput, setSearchInput] = useState<string>('')

  const [statusFilter, setStatusFilter] = useState<
    'saved' | 'archived' | 'completed' | 'all'
  >('saved')

  const filteredAndSearchedItems = useMemo(() => {
    return filteredData.filter((item) => {
      const searchPattern = searchInput.toLowerCase()
      return (
        item?.name?.toLowerCase().includes(searchPattern) ||
        item.campaign?.toLowerCase().includes(searchPattern)
      )
    })
  }, [filteredData, searchInput])

  const {
    data: keywordsWithStatsData,
    isLoading: keywordsLoading,
    refetch: refetchKeywordsWithStats,
  } = useGetKeywordsWithStatsQuery({
    end: endDate,
    start: startDate,
  })

  // filteredAndSearchedItems derives from an array that is [] until the
  // response lands, which is indistinguishable from genuinely having no
  // keywords — the page used to claim "No keywords found" mid-flight across a
  // 75vh region. Skeletons cover the first load only.
  const isInitialLoading = keywordsLoading && !keywordsWithStatsData

  const { data: campaignsData, refetch: refetchCampaigns } =
    useGetCampaignsQuery({
      end: endDate,
      start: startDate,
    })

  const heroStats = useMemo(() => {
    const campaigns = campaignsData?.campaigns || []
    const totalMentions = campaigns.reduce(
      (s, c) => s + (c.stats?.mentions || 0),
      0
    )
    const positivePercentage = totalMentions
      ? Math.round(
          (campaigns.reduce(
            (s, c) => s + (c.stats?.positiveMentionsCount || 0),
            0
          ) /
            totalMentions) *
            100
        )
      : 0
    const neutralPercentage = totalMentions
      ? Math.round(
          (campaigns.reduce(
            (s, c) => s + (c.stats?.neutralMentionsCount || 0),
            0
          ) /
            totalMentions) *
            100
        )
      : 0
    const negativePercentage = totalMentions
      ? Math.round(
          (campaigns.reduce(
            (s, c) => s + (c.stats?.negativeMentionsCount || 0),
            0
          ) /
            totalMentions) *
            100
        )
      : 0
    const topKeywords = campaigns
      .flatMap((c) => c.stats?.topKeywords || [])
      .sort((a, b) => (b.mentions || 0) - (a.mentions || 0))
      .filter(
        (kw, index, self) =>
          index ===
          self.findIndex(
            (k) =>
              (k.keyword || '').trim().toLowerCase() ===
              (kw.keyword || '').trim().toLowerCase()
          )
      )
      .slice(0, 6)
    const topSources = campaigns.flatMap((c) => c.stats?.sources || [])
    const totalSources = Array.from(
      new Set(topSources.map((s) => s.source).filter(Boolean))
    ) as string[]

    return {
      negativePercentage,
      neutralPercentage,
      positivePercentage,
      topKeywords,
      topSources,
      totalMentions,
      totalSources,
    }
  }, [campaignsData])

  // First, we see which themes are going to be displayed from themes filter:
  const handleChangeThemesFilter = (val: string[]): void => {
    setThemesFilterArr(val)
  }

  // we will be using this method to get exact num of available keywords
  // from themesFilterArr (array of selected theme ids) from scratch,
  // without depending on filteredData and without changing it.
  // This way we do not intervene in single-direction stream of state and
  // never let it go backwards
  const filterDataByIdsFromThemeSelect = ():
    | KeywordFetchKeywordsWithStats[]
    | undefined => {
    return keywordsWithStatsData?.filter((keyword) => {
      return themesFilterArr.includes(keyword.campaignID)
    })
  }

  const statusFilterSwitch = (
    currentFilter?: 'saved' | 'archived' | 'completed' | 'all'
  ) => {
    const activeFilter = currentFilter ?? statusFilter
    // finding which of the selected themes has relevant status as true and
    // setting filteredData on tab change
    const filterCampaignsByStatus = (selectedStatus: string): void => {
      const filteredKeywordsWithStatsData = filterDataByIdsFromThemeSelect()

      let temp = filteredKeywordsWithStatsData?.filter((item) => {
        return (
          item?.activeStatus !== undefined &&
          item?.activeStatus?.toLowerCase() === selectedStatus.toLowerCase()
        )
      })

      temp = temp ? createCards(temp) : []

      const reorderedTemp = sortFilteredDataByOrder(temp, orderFilter)

      setFilteredData(reorderedTemp ?? [])
    }

    const data = filterDataByIdsFromThemeSelect()

    switch (activeFilter) {
      case 'all': {
        const allSortedCards = data
          ? sortFilteredDataByOrder(createCards(data), orderFilter) ?? []
          : []
        setFilteredData(allSortedCards)
        break
      }

      case 'saved':
        filterCampaignsByStatus('active')
        break

      case 'archived':
        filterCampaignsByStatus('draft')
        break

      case 'completed':
        filterCampaignsByStatus('completed')
        break

      default:
        break
    }
  }

  const handleChangeOrderFilter = (value: string): void => {
    setOrderFilter(value)
  }

  const handleCloseModal = () => {
    setShowModal1(false)
  }

  const clearModalForm = () => {
    setCurrentKeywordInput('')

    setSelectedCampaigns(null)

    setRegisteredKeywordsArr([])

    setAddedKeywordsArr([])
  }

  const closeAndClear = () => {
    setSelectedCampaignForNewKeyword([])

    setShowModal1(false)

    setShowModal2(false)

    clearModalForm()
  }

  const handleNextModal = () => {
    if (selectedCampaignsForNewKeyword?.length) {
      const temp = campaignsData?.campaigns?.filter(
        (item) =>
          !!item._id && !!selectedCampaignsForNewKeyword.includes(item._id)
      )

      if (temp) {
        setSelectedCampaigns(temp)
      }

      handleCloseModal()

      setShowModal2(true)

      setSelectedCampaignForNewKeyword([])
    }
  }

  function addNewKeyword() {
    if (currentKeywordInput.trim().length > 0) {
      const keyword = currentKeywordInput.trim()

      if (!addedKeywordsArr.includes(keyword)) {
        const isDuplicate = selectedCampaigns?.some((campaign) =>
          campaign.keywords?.some((k) => k.name === keyword)
        )

        if (!isDuplicate) {
          setAddedKeywordsArr([...addedKeywordsArr, keyword])
          setCurrentKeywordInput('')
        }
      }
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.stopPropagation()
      event.preventDefault()

      addNewKeyword()
    }
  }

  function handleChangeKeywordSentimentsValue(
    keyword: CampaignPayloadKeyword
  ): void {
    let tempArr = [...registeredKeywordsArr]
    const index = tempArr.findIndex((item) => item.name === keyword.name)

    if (index === -1) {
      tempArr = [...tempArr, keyword]
    } else {
      tempArr = tempArr.filter((item, i) => i !== index)
      tempArr.push(keyword)
    }
    setRegisteredKeywordsArr(tempArr)
  }

  const updateCampaignAndRefetchData = async (
    id: string,
    data: CampaignPayload
  ): Promise<void> => {
    try {
      await AnalyticsApi.v1Private.campaignUpdate(id, data)

      refetchCampaigns()

      refetchKeywordsWithStats()
    } catch (error) {
      toast.error(en.somethingWentWrong)
    }
  }

  const handleFinalizeKeywordForm = async () => {
    if (addedKeywordsArr.length > 0) {
      const newKeywords: CampaignPayloadKeyword[] = addedKeywordsArr.map(
        (name) => {
          const registered = registeredKeywordsArr.find((k) => k.name === name)
          return registered || { name }
        }
      )

      await Promise.all(
        selectedCampaigns?.map((campaign) => {
          const payload: CampaignPayload = {
            from: campaign?.from,
            keywords: [...(campaign?.keywords || []), ...newKeywords],
            name: campaign?.name,
            status: campaign?.status,
            targets: campaign?.targets,
            to: campaign?.to,
          }

          if (campaign?._id) {
            return updateCampaignAndRefetchData(campaign._id, payload)
          }

          return null
        }) || []
      )

      closeAndClear()
    }
  }

  const handleDeleteKeyword = (keyword: string): void => {
    const temp = removeFromArray(addedKeywordsArr, keyword)
    setAddedKeywordsArr(temp)
    const index = registeredKeywordsArr.findIndex(
      (item) => item.name === keyword
    )
    const tempRegistered = registeredKeywordsArr.slice()
    if (index !== -1) {
      tempRegistered.splice(index, 1)
    }
    setRegisteredKeywordsArr(tempRegistered)
  }

  const handleSearchChange = (val: string): void => {
    setSearchInput(val)
  }

  // after that, we filter themes from campaignData by ids and setFilteredData:
  useEffect(() => {
    const filteredDataToSet = filterDataByIdsFromThemeSelect()

    if (filteredDataToSet) {
      setFilteredData(filteredDataToSet)
    }

    // counting numbers for tag buttons in status filter
    const imaginaryCardsForCounting = filteredDataToSet
      ? createCards(filteredDataToSet)
      : undefined

    const activeArr = imaginaryCardsForCounting?.filter((item) => {
      return item.active
    })

    const completedArr = imaginaryCardsForCounting?.filter((item) => {
      return item?.completed
    })

    setActiveNum(activeArr?.length || 0)

    setCompletedNum(completedArr?.length || 0)

    setAllCardsNum(imaginaryCardsForCounting?.length || 0)
  }, [themesFilterArr, keywordsWithStatsData])

  useEffect(() => {
    statusFilterSwitch(statusFilter)
  }, [statusFilter, themesFilterArr, keywordsWithStatsData, orderFilter])

  useEffect(() => {
    if (sessionId) {
      refetchCampaigns()

      refetchKeywordsWithStats()
    }
  }, [sessionId, startDate, endDate])

  return (
    <>
      <div className={styles.keywords}>
        <div className={styles.header}>
          <div className={styles.backButtonWrapper}>
            <PageBackLink label="Social Listening" />
            <PageTitle>{en.keywords}</PageTitle>
          </div>
          <button
            className={styles.addKeywordButton}
            onClick={() => setShowModal1(true)}
          >
            <span className={styles.plusIcon}>+</span>
            Add New Keyword
          </button>
        </div>

        <KeywordsHeroSection
          activeKeywords={activeNum}
          isLifetime={queryParams.period === 'lifetime'}
          isLoading={!campaignsData}
          keywordStats={keywordsWithStatsData ?? undefined}
          negativePercentage={heroStats.negativePercentage}
          neutralPercentage={heroStats.neutralPercentage}
          positivePercentage={heroStats.positivePercentage}
          topKeywords={heroStats.topKeywords}
          totalKeywords={allCardsNum}
          totalMentions={heroStats.totalMentions}
        />

        <TabsSection
          activeTab={statusFilter}
          allCount={allCardsNum}
          archivedCount={0}
          completedCount={completedNum}
          onFilterClick={() => setFilterOpen(!filterOpen)}
          onSearchChange={handleSearchChange}
          onTabChange={setStatusFilter}
          savedCount={activeNum}
          searchValue={searchInput}
        />

        <KeywordsFilterSection
          campaignsData={campaignsData?.campaigns}
          open={filterOpen}
          orderCallback={(value) => {
            if (value) {
              handleChangeOrderFilter(value)
            }
          }}
          orderOptions={ORDER_BY}
          themesCallback={(value) => {
            if (value) {
              handleChangeThemesFilter(value)
            }
          }}
        />

        <div className={styles.cards}>
          {isInitialLoading ? (
            <div className={styles.cardRow}>
              {Array.from({ length: KEYWORD_SKELETON_COUNT }).map(
                (_, index) => (
                  <Skeleton
                    height={KEYWORD_CARD_HEIGHT}
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    radius={KEYWORD_CARD_RADIUS}
                  />
                )
              )}
            </div>
          ) : filteredAndSearchedItems.length ? (
            <div className={styles.cardRow}>
              {filteredAndSearchedItems.map((item) => (
                <KeywordCardItem
                  data={item}
                  key={`${item.name} - ${item.campaign}`}
                />
              ))}
              {(filteredAndSearchedItems.length > 0 ||
                statusFilter === 'saved') && (
                <AddNewCard
                  handleClick={() => setShowModal1(true)}
                  text={en.addNewKeyword}
                />
              )}
            </div>
          ) : statusFilter === 'saved' ? (
            <div className={styles.cardRow}>
              <AddNewCard
                handleClick={() => setShowModal1(true)}
                text={en.addNewKeyword}
              />
            </div>
          ) : (
            <div className={styles.empty}>{en.noKeywordsFound}</div>
          )}
        </div>
      </div>

      <BaseModal isOpen={!!showModal1}>
        <ChooseCampaignModalContent
          campaignsData={campaignsData}
          closeAndClear={closeAndClear}
          handleNextModal={handleNextModal}
          selectedCampaignForNewKeyword={selectedCampaignsForNewKeyword}
          setSelectedCampaignForNewKeyword={(ids) => {
            setSelectedCampaignForNewKeyword(ids)
          }}
        />
      </BaseModal>

      <BaseModal isOpen={!!showModal2}>
        <AddNewKeywordModalContent
          addNewKeyword={() => addNewKeyword()}
          addedKeywordsArr={addedKeywordsArr}
          closeAndClear={closeAndClear}
          currentKeywordInput={currentKeywordInput}
          handleChangeKeywordSentimentsValue={(keywordData) =>
            handleChangeKeywordSentimentsValue(keywordData)
          }
          handleDeleteKeyword={handleDeleteKeyword}
          handleFinalizeKeywordForm={handleFinalizeKeywordForm}
          handleKeyDown={handleKeyDown}
          onGoBack={() => {
            setShowModal2(false)
            setShowModal1(true)
          }}
          setCurrentKeywordInput={setCurrentKeywordInput}
        />
      </BaseModal>
    </>
  )
}
